import type { ReactNode } from 'react';
import type { Metric } from '../../api/types.js';
import { color, font, fontSize } from '../../theme/tokens.js';
import { formatValue, formatValueExact } from './format.js';
import { Disclosure } from '../Disclosure/Disclosure.js';
import {
  BreakdownPanel,
  ProvenanceFootnote,
  type BreakdownRow,
} from '../Breakdown/Breakdown.js';
import { provenanceFor } from '../Breakdown/provenance.js';

/**
 * A numeric metric cell (FR-054, FR-055, FR-062, R-14).
 *
 * Three rules it exists to make impossible to get wrong:
 *
 * - **A value that cannot be computed renders `—`, never `0` and never blank** (FR-055). The
 *   two are different facts, and a zero that means "we do not know" is the kind of number a
 *   leader acts on.
 * - **A `null` delta renders NO arrow** — not a neutral one and not a zero one (FR-054). An
 *   arrow asserts a comparison; where there is no comparable prior window there is nothing to
 *   assert.
 * - **BOTH the 30-day and the 90-day change stay reachable**, through `aria-describedby` rather
 *   than only as a visual arrow (FR-062). Since 2026-09-27 they are also drawn in the hover
 *   bubble again, as their own labelled section — see `<TrendSection>` for why that is not the
 *   line the 2026-09-02 owner request removed.
 *
 * Trend values are fixed trailing windows from `asOf` (R-12), so they do **not** move when the
 * period or grain selector moves. If an arrow's *figure* changes with the period filter, FR-054
 * is broken. Whether an arrow is drawn at all is the caller's call — see `showTrendArrow`.
 */

export {
  EM_DASH,
  COMPACT_FROM,
  formatNumber,
  formatNumberExact,
  formatNumberNarrow,
  formatValue,
  formatValueExact,
  isCompacted,
  money,
  moneyExact,
} from './format.js';

export interface MetricCellProps {
  metric: Metric | null | undefined;
  /** The column's full label, so the announced name says what the number is. */
  label: string;
  format?: (value: number) => string;
  /** Rendered after the value, e.g. the `(27)` openings parenthetical on `#JOBS`. */
  suffix?: string;
  align?: 'left' | 'right' | 'center';
  bold?: boolean;
  colorOverride?: string;
  /**
   * Rows for the hover breakdown (blueprint § tooltip). When present the panel shows the value
   * decomposed by axis instead of just restating it.
   *
   * Passed through `<Disclosure panel>` rather than rendered here, so the breakdown reaches
   * keyboard focus and assistive technology on exactly the same terms as pointer hover
   * (FR-062) — a bespoke hover div would be pointer-only.
   */
  breakdown?: BreakdownRow[];
  /**
   * The metric's key in the served block — `activeToa`, `covPct`, `subs` — which is what the
   * panel's provenance footer is looked up on (owner request 2026-09-10).
   *
   * The **key** and not the `label`, because a label is a presentation decision that moves: the
   * one figure is `ACTV` here, `TOA` on the by-client table and `Active TOA` on a Delivery tile,
   * and keying the lineage on any of those would let three surfaces describe one number three
   * ways. Every caller already has the key in hand — it is what indexes `metric` out of the
   * block a line above.
   *
   * Omitted, or unknown to `provenanceFor`, renders **no footer at all** rather than a guess.
   * The footer is an assertion about where a number came from, printed in the place a reader
   * goes when they already do not trust it, so a wrong one costs more than a missing one.
   */
  metricKey?: string;
  /**
   * Whether the visible trend — the arrow **and the 30-day percentage beside it**, `▲12%` — is
   * drawn. Defaults to `true`.
   *
   * ⚠ **Pass it only on a column that can carry a trend.** It reserves `TREND_SLOT_PX` (36px) in
   * every cell of the column whether or not that row has a delta, which is what keeps a column's
   * figures aligned — and on a column whose metric is never trended (a headcount, a book bucket)
   * that is 36px of nothing pushing every figure off its header. It was an 8px arrow slot until
   * 2026-09-27 and cheap enough to reserve everywhere; it is not any more.
   *
   * Set `false` where the trend would assert a comparison the cell is not making. Two callers do:
   *
   * - a column that is a **decomposition** of a trended column rather than a trended figure of
   *   its own — Delivery's `Subs-I`, `Ofrs-T`, `GM-I` and the rest. PRD-Delivery §9.1 puts arrows
   *   on six columns; twenty-one arrows in a row asserts twenty-one comparisons where the reader
   *   was offered six;
   * - a table showing a **to-date period** — Region Performance on MTD or YTD, via
   *   `showsTrendArrows` (PRD §9.5, amended 2026-08-28). The delta is a 30-day trailing window
   *   and the value beside it is a partial calendar span, so the pair describes two different
   *   windows while looking like one.
   *
   * It suppresses the **face only**. The 30- and 90-day figures stay in the hover panel's trend
   * section and in `aria-describedby`, so nothing becomes unreachable — the face is a summary,
   * not the data.
   */
  showTrendArrow?: boolean;
}

/**
 * The trend's reserved width, always occupied when the column draws trends at all.
 *
 * Without a reservation the number's position depended on whether *that row* had a delta: measured
 * on Region Performance, a figure with an arrow sat 14px inside the column's right edge and one
 * without sat 4px — so `115` did not line up under `125 ▲` in the same column, a 10px jitter that
 * read as the numbers being out of alignment with their header.
 *
 * **Derived from the face's ceiling, not from the data** — the lesson `TWO_FIGURE_TRACK` learned
 * twice. `trendFace` never prints more than five glyphs (`>999%`), at `TREND_FONT_PX` in IBM Plex
 * Mono's fixed 600/1000-em advance, after the 8px the arrow alone always had and a 1px gap:
 * `8 + 1 + 5 × 5.4 = 36`. A narrower slot is not overlapped, it is cut by the cell's
 * `overflow: hidden` — silently, which is the failure every width in these tables exists to stop.
 *
 * Reserved only when `showTrendArrow` is true. Where a caller has switched trends off for the whole
 * column — Delivery's decomposition columns, Region Performance on MTD/YTD — there is nothing to
 * reserve for, and holding the space would just push every figure off its own header.
 */
const ARROW_PX = 8;
const TREND_FONT_PX = 9;
const TREND_MAX_GLYPHS = 5;
export const TREND_SLOT_PX = Math.ceil(ARROW_PX + 1 + TREND_MAX_GLYPHS * TREND_FONT_PX * 0.6);

function formatDelta(delta: number | null | undefined): string | null {
  if (delta === null || delta === undefined || Number.isNaN(delta)) return null;
  const sign = delta > 0 ? '+' : delta < 0 ? '−' : '';
  return `${sign}${Math.abs(delta)}%`;
}

/** Green up, red down (Principle I, blueprint conventions). No arrow at all for `null`. */
function arrowFor(delta: number | null | undefined): { glyph: string; color: string } | null {
  if (delta === null || delta === undefined || Number.isNaN(delta) || delta === 0) return null;
  return delta > 0
    ? { glyph: '▲', color: color.positive }
    : { glyph: '▼', color: color.negative };
}

/**
 * The percentage printed beside the arrow in the cell — whole percent, **no sign**, at most five
 * glyphs (owner request 2026-09-27).
 *
 * - **No sign**, because the arrow is the sign: `▲+12%` says it twice in a cell budgeted to the
 *   glyph, and a `−` beside a red `▼` is a double negative a reader has to resolve.
 * - **Whole percent**, because the cell is a summary and the panel carries the stored decimal.
 * - **`<1%` rather than `0%`** below one: the arrow is drawn for any non-zero delta, and `▲0%` is
 *   an arrow contradicting its own figure — the same rule `changeFace` keeps on the chart strip.
 * - **`>999%` above that**, which a small scope reaches honestly (one submission last month,
 *   fifteen this month is `+1400%`). The slot is sized to these five glyphs; a longer face would
 *   be cut by `overflow: hidden` rather than wrap.
 *
 * `null` exactly where `arrowFor` is — no delta, or a zero one — so the two cannot disagree.
 */
export function trendFace(delta: number | null | undefined): string | null {
  if (delta === null || delta === undefined || Number.isNaN(delta) || delta === 0) return null;
  const magnitude = Math.abs(delta);
  if (magnitude < 1) return '<1%';
  const rounded = Math.round(magnitude);
  return rounded > 999 ? '>999%' : `${rounded}%`;
}

/**
 * Metrics compared as-of rather than summed over a window (`stockTrend` in the ingest), so their
 * trend is "now against 30 days ago" rather than "the last 30 days against the 30 before". Keyed
 * on the metric key, as the provenance footer is: `activeToa` on Region Performance and Delivery,
 * `toa` / `toaInternal` on the by-client breakdown.
 */
const STOCK_KEYS: ReadonlySet<string> = new Set(['activeToa', 'toa', 'toaInternal']);

/**
 * The hover panel's trend section (owner request 2026-09-27): both windows, each named.
 *
 * ⚠ **This is not the line the 2026-09-02 owner request removed, and the difference is the point.**
 * That was `30-day change +7%. 90-day change −3%` trailing the breakdown rows in the same type, so
 * it read as two more parts of the decomposition above it — on windows the decomposition is not
 * cut on. This one is fenced off by a rule, carries its own heading, and says in words that its
 * windows are fixed and **not the table period** — the one misreading a `+12%` beside a `12M`
 * figure invites.
 *
 * ⚠ **Only the glyph is coloured.** The theme's green is ~3.3:1 on the tooltip's yellow — fine for
 * a glyph, short of AA for text — so the figure stays in the panel's own ink.
 *
 * `aria-hidden`, because `trendDescription` already reaches assistive technology through
 * `aria-describedby`, and `<Disclosure>` also describes the trigger by the panel: without this the
 * two figures would be announced twice.
 */
function TrendSection({
  metric,
  metricKey,
}: {
  metric: Metric | null | undefined;
  metricKey: string | undefined;
}) {
  if (!metric || (formatDelta(metric.d30) === null && formatDelta(metric.d90) === null)) {
    return null;
  }

  const stock = metricKey !== undefined && STOCK_KEYS.has(metricKey);
  const rows = [
    { label: stock ? 'Now vs 30 days ago' : 'Last 30 days vs prior 30', delta: metric.d30 },
    { label: stock ? 'Now vs 90 days ago' : 'Last 90 days vs prior 90', delta: metric.d90 },
  ];

  return (
    <span
      aria-hidden="true"
      style={{
        display: 'block',
        marginTop: 6,
        paddingTop: 5,
        borderTop: `1px solid ${color.tipBorderInner}`,
      }}
    >
      <span
        style={{
          display: 'block',
          fontSize: fontSize.micro,
          fontWeight: 700,
          letterSpacing: '.2px',
          color: color.tipInk,
        }}
      >
        Trend
      </span>
      <span
        style={{
          display: 'block',
          maxWidth: 216,
          marginBottom: 3,
          fontSize: fontSize.micro,
          fontWeight: 500,
          lineHeight: 1.4,
          color: color.tipInkRow,
        }}
      >
        Fixed windows ending today — not the table period
      </span>
      {rows.map((row) => {
        const arrow = arrowFor(row.delta);
        const magnitude = formatDelta(row.delta);
        return (
          <span
            key={row.label}
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              gap: 12,
              fontSize: fontSize.captionStrong,
              padding: '1.5px 0',
            }}
          >
            <span>{row.label}</span>
            <span className="mono" style={{ fontFamily: font.mono, fontWeight: 700 }}>
              {arrow ? (
                <span style={{ color: arrow.color, fontSize: '8.5px', marginRight: 3 }}>
                  {arrow.glyph}
                </span>
              ) : null}
              {/* The magnitude only — the glyph carries the direction, as it does in the cell. A
                  zero change has no glyph and prints `0%`; an unavailable one prints `—`. */}
              {magnitude === null ? '—' : magnitude.replace(/^[+−]/, '')}
            </span>
          </span>
        );
      })}
    </span>
  );
}

export function trendDescription(metric: Metric | null | undefined): string | undefined {
  if (!metric) return undefined;

  const d30 = formatDelta(metric.d30);
  const d90 = formatDelta(metric.d90);
  if (!d30 && !d90) return undefined;

  // Both figures, always — the tooltip must state each of them (FR-054).
  return [
    d30 ? `30-day change ${d30}` : '30-day change unavailable',
    d90 ? `90-day change ${d90}` : '90-day change unavailable',
  ].join('. ');
}

export function MetricCell({
  metric,
  label,
  format,
  suffix,
  align = 'right',
  bold = false,
  colorOverride,
  breakdown,
  metricKey,
  showTrendArrow = true,
}: MetricCellProps) {
  const value = metric?.value ?? null;
  const display = formatValue(value, format);
  const arrow = showTrendArrow ? arrowFor(metric?.d30) : null;
  const arrowFace = showTrendArrow ? trendFace(metric?.d30) : null;
  const description = trendDescription(metric);
  const trend: ReactNode = <TrendSection metric={metric} metricKey={metricKey} />;

  /*
   * The panel and the announced name carry the **exact** figure, so compacting the visible one
   * hides no digits from anybody: `20.1K` on screen, `20,135` on hover and to a screen reader
   * (FR-062). Where nothing was compacted the two strings are identical and this costs nothing.
   */
  const exactDisplay = formatValueExact(value, format);

  /*
   * The lineage footer, on BOTH panel shapes — the decomposed one and the bare label/value one.
   *
   * Attaching it to the breakdown alone would mean the columns that carry no axes (`LDR`, `MGR`,
   * `TRGT`, every Delivery decomposition column) were the ones that never said where they came
   * from, and those are exactly the figures a reader has no other way to place.
   */
  const provenance = provenanceFor(metricKey);

  const accessibleName =
    value === null
      ? `${label}: no data`
      : `${label}: ${exactDisplay}${suffix ? ` ${suffix}` : ''}`;

  return (
    <div
      style={{
        display: 'flex',
        justifyContent:
          align === 'right' ? 'flex-end' : align === 'center' ? 'center' : 'flex-start',
        alignItems: 'center',
        padding: '8px 4px',
        overflow: 'hidden',
      }}
    >
      <Disclosure
        accessibleName={accessibleName}
        description={description}
        panel={
          breakdown && breakdown.length > 0 ? (
            <BreakdownPanel
              title={label}
              total={`${exactDisplay}${suffix ? ` ${suffix}` : ''}`}
              rows={breakdown}
              trend={trend}
              provenance={provenance}
            />
          ) : (
            <span>
              <strong>{label}</strong>
              <span style={{ display: 'block' }}>
                {exactDisplay}
                {suffix ? ` ${suffix}` : ''}
              </span>
              {trend}
              <ProvenanceFootnote provenance={provenance} />
            </span>
          )
        }
      >
        <span
          className="mono"
          style={{
            fontFamily: font.mono,
            fontSize: fontSize.captionStrong,
            fontWeight: bold ? 600 : 500,
            color: colorOverride ?? (value === null ? color.inkFaint : color.inkNumeric),
            whiteSpace: 'nowrap',
          }}
        >
          {display}
          {/*
            No space before the suffix on the VISIBLE face — `164(176)` — but the space is kept in
            the accessible name and in the panel above (owner request 2026-08-30).
            
            The two are not the same problem. The face is the only space-constrained one: `#JOBS`
            is the only column carrying two figures and it was overrunning its track, so a
            character back is a character earned. The announced name and the panel have no width
            limit, and "164(176)" is materially worse to hear than "164 (176)" — a screen reader
            runs the two figures together into one token. So they diverge deliberately, and the
            space in `accessibleName` is not a leftover to tidy up for consistency.
          */}
          {suffix ? <span style={{ color: color.inkFaint }}>{suffix}</span> : null}
        </span>
        {showTrendArrow ? (
          /*
            Always rendered when the column draws trends — empty when this row has no delta — so
            the figure beside it never moves. `flex: none` on a fixed width is what makes the slot
            hold; a bare `width` on a flex child can still be shrunk.

            The arrow, then the 30-day percentage (owner request 2026-09-27), one colour for both
            so they read as one mark. Only d30 is on the face: it is what the arrow has always
            summarised, and the 90-day figure is in the panel.

            `aria-hidden`: the face is a summary of `trendDescription`, which reaches assistive
            technology through `aria-describedby` on the trigger. An empty slot would otherwise
            announce as nothing at all, and a full one would duplicate what was already said.
          */
          <span
            aria-hidden="true"
            style={{
              flex: `0 0 ${TREND_SLOT_PX}px`,
              display: 'inline-flex',
              alignItems: 'baseline',
              gap: 1,
              color: arrow?.color,
              textAlign: 'left',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
            }}
          >
            <span style={{ flex: `0 0 ${ARROW_PX}px`, fontSize: '8.5px' }}>
              {arrow?.glyph ?? ''}
            </span>
            {arrowFace ? (
              <span
                className="mono"
                style={{
                  fontFamily: font.mono,
                  fontSize: `${TREND_FONT_PX}px`,
                  fontWeight: 600,
                }}
              >
                {arrowFace}
              </span>
            ) : null}
          </span>
        ) : null}
      </Disclosure>
    </div>
  );
}
